import KML from 'ol/format/KML';
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type { AttributeValue, OriginalAttributes } from '../core/types';
import { buildLayerModel, type ParsedPhysicalFeature } from '../domain/hierarchy';
import { parseDescriptionTable } from './descriptionTable';

const ignoredProperties = new Set(['geometry', 'description', 'styleUrl', 'Style', 'name']);

const normalizeValue = (value: unknown): AttributeValue => {
  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (value === undefined) return null;
  return String(value);
};

export function parseKmlFeatures(kmlText: string, options: { includeRawDescription?: boolean } = {}): ParsedPhysicalFeature[] {
  const formatter = new KML({ extractStyles: false, showPointNames: false });
  const features = formatter.readFeatures(kmlText, {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857',
  }) as Feature<Geometry>[];

  return features.flatMap((feature, sourceIndex) => {
    if (!feature.getGeometry()) return [];
    const properties = feature.getProperties();
    const fromDescription = parseDescriptionTable(
      typeof properties.description === 'string' ? properties.description : undefined,
    );
    const original: Record<string, AttributeValue> = { ...fromDescription };
    if (options.includeRawDescription && typeof properties.description === 'string') original.description = properties.description;
    Object.entries(properties).forEach(([key, value]) => {
      if (!ignoredProperties.has(key)) original[key] = normalizeValue(value);
    });
    if (!('Nombre' in original) && typeof properties.name === 'string') original.Nombre = properties.name;
    return [{ feature, attributes: original satisfies OriginalAttributes, sourceIndex }];
  });
}

export function parseKml(kmlText: string, sourceName: string, sourceFormat: 'KML' | 'KMZ') {
  return buildLayerModel(sourceName, sourceFormat, parseKmlFeatures(kmlText));
}
